import { useEffect, useRef, useState } from 'react';
import { Alert, AlertIcon, Box, Input, Text } from '@chakra-ui/react';
import {
  MAX_SPREADSHEET_BYTES,
  parseStudentSpreadsheet,
} from '@grcs/utils/users/studentSpreadsheet';

interface Props {
  onDataParsed: (rows: Record<string, string>[]) => void;
  onFileChange: () => void;
}

export default function SpreadsheetReader({
  onDataParsed,
  onFileChange,
}: Props) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const request = useRef(0);
  useEffect(
    () => () => {
      request.current++;
    },
    [],
  );

  const readFile = async (file?: File) => {
    if (!file) return;
    const current = ++request.current;
    onFileChange();
    setError('');
    setBusy(true);
    try {
      if (file.size > MAX_SPREADSHEET_BYTES)
        throw new Error('Choose a file smaller than 10 MB.');
      const result = await parseStudentSpreadsheet(
        await file.arrayBuffer(),
        file.name,
      );
      if (current === request.current) onDataParsed(result.rows);
    } catch (error) {
      if (current === request.current)
        setError(
          error instanceof Error ? error.message : 'Could not read the file.',
        );
    } finally {
      if (current === request.current) setBusy(false);
    }
  };

  return (
    <Box
      borderWidth="2px"
      borderStyle="dashed"
      borderRadius="md"
      p={4}
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => {
        event.preventDefault();
        if (!busy) void readFile(event.dataTransfer.files[0]);
      }}
    >
      <Text
        as="label"
        htmlFor="student-spreadsheet"
        display="block"
        fontSize="sm"
        mb={2}
      >
        Drop a file here or choose a file
      </Text>
      <Input
        id="student-spreadsheet"
        type="file"
        accept=".csv,.xlsx,.xls"
        disabled={busy}
        p={1}
        height="auto"
        border="none"
        onChange={(event) => {
          void readFile(event.target.files?.[0]);
          event.target.value = '';
        }}
      />
      <Text fontSize="xs" color="gray.600" mt={2}>
        CSV, XLSX, or XLS · Maximum 10 MB. Excel imports the first worksheet
        only.
      </Text>
      {busy && (
        <Text role="status" fontSize="sm" mt={2}>
          Reading file…
        </Text>
      )}
      {error && (
        <Alert status="error" mt={3} fontSize="sm">
          <AlertIcon />
          {error}
        </Alert>
      )}
    </Box>
  );
}
